import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type NotificationName =
    | 'auth-notification'
    | 'pwa-prompt'
    | 'push-subscription-prompt'
    | 'pwa-prompt-game'
    | 'error-message'
    | 'install-google-play-modal'
    | 'play-on-app-modal'
    | 'sign-in-modal'

export const SIGN_IN_MODAL_WEIGHT = 3
// one tap outranks the prompts we schedule ourselves and is never cancelled by the queue
export const ONE_TAP_WEIGHT = SIGN_IN_MODAL_WEIGHT + 1
// an auth error tops everything, otherwise request() drops the toast and the user gets no feedback at all
export const AUTH_ERROR_TOAST_WEIGHT = ONE_TAP_WEIGHT + 1

export type NotificationCloseReason = 'removed' | 'preempted' | 'disposed'

export type NotificationItem = {
    name: NotificationName
    weight: number
    onOpen: () => void
    onClose: (reason: NotificationCloseReason) => void
}

export const useNotificationsQueue = defineStore('notification-queue', () => {
    // queue state
    const notificationsQueue = ref<NotificationItem[]>([])
    const activeName = ref<NotificationName | null>(null)
    const initialized = ref(false)
    // helpers
    const activeItem = computed<NotificationItem | null>(() =>
        activeName.value ? (notificationsQueue.value.find((n) => n.name === activeName.value) ?? null) : null,
    )

    function sortQueueDescByWeight() {
        notificationsQueue.value.sort((a, b) => b.weight - a.weight)
    }

    function existingItem(name: NotificationName) {
        return notificationsQueue.value.find((item) => item.name === name)
    }

    function dispatch() {
        if (!initialized.value || activeName.value || !notificationsQueue.value.length) {
            return
        }
        sortQueueDescByWeight()
        const next = notificationsQueue.value[0]
        const afterNext = notificationsQueue.value[1]

        if (!next) {
            return
        }

        activeName.value = next.name
        if (afterNext && next.weight > afterNext.weight) {
            forceCloseAndClearNotActive()
        }
        next.onOpen()
    }

    function remove(name: string) {
        const item = notificationsQueue.value.find((n) => n.name === name)
        item?.onClose('removed')
        notificationsQueue.value = notificationsQueue.value.filter((n) => n.name !== name)
        if (activeName.value === name) {
            activeName.value = null
            dispatch()
        }
    }

    function forceCloseAndClearAll(reason: NotificationCloseReason) {
        for (const n of notificationsQueue.value) {
            n.onClose(reason)
        }
        notificationsQueue.value = []
        activeName.value = null
    }

    function dispose() {
        initialized.value = false
        forceCloseAndClearAll('disposed')
    }

    function forceCloseAndClearNotActive() {
        for (const n of notificationsQueue.value) {
            if (n.name !== activeName.value) {
                n.onClose('preempted')
            }
        }
        notificationsQueue.value = notificationsQueue.value.filter((n) => n.name === activeName.value)
    }

    function request(payload: NotificationItem) {
        if (activeItem.value && payload.weight < activeItem.value.weight) {
            return
        }
        if (activeItem.value && payload.weight > activeItem.value.weight) {
            forceCloseAndClearAll('preempted')
        }
        if (!existingItem(payload.name)) {
            notificationsQueue.value.push({
                name: payload.name,
                weight: payload.weight,
                onOpen: payload.onOpen,
                onClose: payload.onClose,
            })
        }
        dispatch()
    }

    function init() {
        initialized.value = true
        dispatch()
    }

    return {
        // state
        notificationsQueue,
        activeName,
        // api
        request,
        dispose,
        remove,
        init,
    }
})
